import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "antd";
import { ArrowRightOutlined } from "@ant-design/icons";
import { motion } from "framer-motion";
import {
    swFade,
    swFloatUp,
    swStagger,
    swViewport,
    swZoom,
} from "../components/software/shared/softwareMotion";
import { scebChannels, scebReachStats, scebWhyChoose } from "../data/softwarePagesContent";
import "../styles/software-pages-base.css";
import "../styles/sms-broadcast.css";

export const SmsBroadcastPage = () => {
    const navigate = useNavigate();

    return (
        <div className="sw-page sceb-page">
            {/* §1 Multi-channel hero ribbon */}
            <section className="sceb-ribbon">
                <div className="sw-container">
                    <motion.div
                        className="sceb-ribbon__head"
                        initial="hidden"
                        whileInView="visible"
                        viewport={swViewport}
                        variants={swFade}
                    >
                        <h1 className="sceb-hero-title">SMS • Call • Email Broadcast</h1>
                        <h2 className="sceb-tagline">One Platform for Every Outbound Channel</h2>
                        <p className="sceb-lead">
                            Reach customers by text, voice, or email — from a single campaign
                            builder with unified reporting and compliance controls.
                        </p>
                    </motion.div>
                    <motion.div
                        className="sceb-ribbon__channels"
                        initial="hidden"
                        whileInView="visible"
                        viewport={swViewport}
                        variants={swStagger}
                    >
                        {scebChannels.map((ch, i) => {
                            const Icon = ch.icon;
                            return (
                                <motion.article
                                    key={ch.name}
                                    className={`sceb-channel sceb-channel--${ch.name.toLowerCase()}`}
                                    variants={swFloatUp}
                                    custom={i * 0.1}
                                >
                                    <div className="sceb-channel__wave" aria-hidden />
                                    <Icon className="sceb-channel__icon" aria-hidden />
                                    <h2>{ch.name}</h2>
                                    <strong>{ch.stat}</strong>
                                    <span>{ch.label}</span>
                                </motion.article>
                            );
                        })}
                    </motion.div>
                </div>
            </section>

            {/* §2 Why Businesses Choose Multi-Channel Broadcasting */}
            <section className="sceb-why">
                <div className="sw-container">
                    <motion.div
                        className="sceb-section-head sceb-section-head--center"
                        initial="hidden"
                        whileInView="visible"
                        viewport={swViewport}
                        variants={swFloatUp}
                    >
                        <h2 className="sceb-h2">Why Businesses Choose Multi-Channel Broadcasting</h2>
                        <p className="sceb-sub">
                            Reach customers through their preferred communication channel while managing
                            everything from a single platform.
                        </p>
                    </motion.div>
                    <motion.div
                        className="sceb-why__grid"
                        initial="hidden"
                        whileInView="visible"
                        viewport={swViewport}
                        variants={swStagger}
                    >
                        {scebWhyChoose.map((item, i) => {
                            const Icon = item.icon;
                            return (
                                <motion.article
                                    key={item.id}
                                    className="sceb-why__card"
                                    variants={swZoom}
                                    custom={i * 0.08}
                                    whileHover={{ y: -6, transition: { duration: 0.28 } }}
                                >
                                    <div className="sceb-why__icon" aria-hidden>
                                        <Icon />
                                    </div>
                                    <h3>{item.title}</h3>
                                    <p>{item.desc}</p>
                                </motion.article>
                            );
                        })}
                    </motion.div>
                </div>
            </section>

            {/* §3 Reach analytics split */}
            <section className="sceb-reach">
                <div className="sw-container sceb-reach__grid">
                    <motion.div
                        className="sceb-reach__copy"
                        initial="hidden"
                        whileInView="visible"
                        viewport={swViewport}
                        variants={swFloatUp}
                    >
                        <h2 className="sceb-h2 sceb-h2--light">Delivery Analytics</h2>
                        <p className="sceb-sub sceb-sub--light">
                            Track delivery rates, answer rates, and open rates in one dashboard.
                            Compare channel performance and optimize spend.
                        </p>
                    </motion.div>
                    <motion.div
                        className="sceb-reach__bars"
                        initial="hidden"
                        whileInView="visible"
                        viewport={swViewport}
                        variants={swStagger}
                    >
                        {scebReachStats.map((stat) => (
                            <motion.div key={stat.channel} className="sceb-reach__row" variants={swFloatUp}>
                                <span className="sceb-reach__label">{stat.channel}</span>
                                <div className="sceb-reach__track">
                                    <motion.div
                                        className={`sceb-reach__fill sceb-reach__fill--${stat.channel.toLowerCase()}`}
                                        initial={{ width: 0 }}
                                        whileInView={{ width: `${stat.rate}%` }}
                                        viewport={swViewport}
                                        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                                    />
                                </div>
                                <span className="sceb-reach__pct">{stat.rate}%</span>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </section>

            <section className="sw-cta">
                <div className="sw-container">
                    <div className="sw-cta__inner">
                        <h2 className="sw-cta__title">Launch your next broadcast</h2>
                        <p className="sw-cta__desc">SMS, voice, and email campaigns with unified scheduling and analytics.</p>
                        <Button className="sw-btn" onClick={() => navigate("/contact")}>
                            Start Broadcasting <ArrowRightOutlined />
                        </Button>
                    </div>
                </div>
            </section>
        </div>
    );
};
